import { PlanType } from "src/apis/subscriptionApi";
import { PlanForm } from "../PlanDrawer/PlanDrawer.types";
import { attendanceLimitOptions, classAccessOptions, clientStatusOptions, intervalTypeOptions, sessionRecurringOptions } from "../PlanDrawer/PlanDrawer.utils";
import { PlanTypeApiName, PlanTypeSlugs } from "../plan.types";

// TODO: Replace this hardcoded general class uuid with default client general class
const GENERAL_CLASS_UUID = "cit_00000000000000";
export const COUNTRIES_WITH_PRICE_RECS = [
  "United States",
  "Canada",
  "Australia",
  "United Kingdom",
];


export type PlanTemplate = {
  id: PlanTypeSlugs;
  apiName?: PlanTypeApiName;
  name: string;
  description: string;
  recommendedPrice: number | null;
  benefits: string[];
  planType: PlanType;
  frequency: "per month" | "single session" | "10 sessions";
  planFormValues: Partial<PlanForm>;
};

const sharedPlanFormValues: Partial<PlanForm> = {
  publishToPublic: true,
  hybridAf: false,
  sellOnMemberApp: true,
  sellOnStaffApp: true,
  sellOnWebsitePlanList: true,
  allowClassBooking: true,
};

export const UNLIMITED_PLAN_TEMPLATE: PlanTemplate = {
  id: "unlimited",
  name: "Unlimited",
  apiName: "Unlimited",
  description: "Unlimited access to classes and open gym. Your highest tier plan for your most engaged clients.",
  benefits: ["Unlimited classes and open gym", "Recurring plan, billed monthly"],
  recommendedPrice: null,
  planType: "recurring",
  frequency: "per month",
  planFormValues: {
    name: "Unlimited",
    clientStatus: clientStatusOptions[0],
    price: 0,
    billEveryCount: 1,
    billEveryInterval: intervalTypeOptions[2],
    openGym: true,
    recursForever: true,
    attendanceLimit: attendanceLimitOptions[0],
    classAccess: classAccessOptions[0],
    sessionsPeriod: sessionRecurringOptions[0],
    sessionsExpireInterval: intervalTypeOptions[0],
    shortDescription: "Unlimited access to all classes and open gym sessions. Our premium membership for dedicated fitness enthusiasts.",
    longDescription: `<p><strong>Unlimited Membership Details</strong></p><p>Our premium Unlimited membership gives you complete access to our entire facility and class schedule with no restrictions. This is our highest value plan designed for members who are committed to their fitness journey.</p><p><strong>What is included:</strong></p><p>- Unlimited access to all classes on our schedule</p><p>- Unlimited open gym access during regular hours</p><p>- Priority booking for classes</p><p>- Access to member-only events and challenges</p><p><strong>Billing Information:</strong></p><p>- Monthly recurring payment</p><p>- Automatically renews each month</p><p>New members receive a complimentary onboarding session to familiarize themselves with our equipment and class formats. We recommend booking classes in advance to secure your spot, though same-day bookings are often available.</p><p>Questions? Our friendly staff is always available to help you make the most of your membership.</p>`,
    confirmationMessage: `<p>Thank you for joining our Unlimited membership plan! We are excited to welcome you to our community.</p><p>With your Unlimited membership, you now have:</p><p>- Unlimited access to all classes</p><p>- Full open gym privileges</p><p>- Priority booking for special events</p><p>To get the most out of your membership, download our member app to view the class schedule and reserve your spot. Your billing will occur monthly, and you can manage your account through the app or website.</p><p>We look forward to seeing you at the gym! If you have any questions, our staff is always here to help.</p>`,
    ...sharedPlanFormValues,
  },
};

export const LIMITED_CLASS_PLAN_TEMPLATE: PlanTemplate = {
  id: "limited-class",
  name: "12 Classes a Month",
  apiName: "Limited Class",
  description: "Access to 12 classes a month. Your mid-tier plan for clients who know exactly what they want.",
  benefits: ["12 classes a month", "Recurring plan, billed monthly"],
  recommendedPrice: null,
  planType: "recurring",
  frequency: "per month",
  planFormValues: {
    name: "12 Classes a Month",
    clientStatus: clientStatusOptions[0],
    price: 0,
    billEveryCount: 1,
    billEveryInterval: intervalTypeOptions[2],
    openGym: false,
    recursForever: true,
    attendanceLimit: attendanceLimitOptions[1],
    classAccess: classAccessOptions[1],
    sessionsCount: 12,
    classes: [{ uuid: GENERAL_CLASS_UUID }],
    sessionsPeriod: sessionRecurringOptions[0],
    sessionsExpireInterval: intervalTypeOptions[0],
    shortDescription: "12 classes per month with flexible scheduling. Perfect for consistent training with a predictable monthly routine.",
    longDescription: `<p><strong>12 Classes a Month Membership Details</strong></p><p>Our 12 Classes a Month plan provides the perfect balance of structure and flexibility. Ideal for those who want consistent training without committing to unlimited access.</p><p><strong>What is included:</strong></p><p>- 12 class credits per month to use across our schedule</p><p>- Credits reset on your billing date each month</p><p>- Access to all standard class types</p><p><strong>Billing Information:</strong></p><p>- Monthly recurring payment</p><p>- Automatically renews each month</p><p>New members receive a complimentary onboarding session to learn about our equipment and class formats. We recommend tracking your usage through our app to maximize your monthly classes.</p>`,
    confirmationMessage: `<p>Thank you for signing up for our 12 Classes a Month plan! We're thrilled to have you join our fitness community.</p><p>Your plan includes:</p><p>- 12 class sessions per month</p><p>- Access to all class types</p><p>- Ability to roll over up to 2 unused classes (conditions apply)</p><p>Remember to reserve your classes in advance through our app or website to secure your spot. Your monthly allocation resets on your billing date.</p><p>We're committed to helping you achieve your fitness goals and look forward to seeing you in class soon!</p>`,
    ...sharedPlanFormValues,
  },
};

export const PUNCH_CARD_PLAN_TEMPLATE: PlanTemplate = {
  id: "punch-card",
  name: "10 Session Punch Card",
  apiName: "Punch Card",
  description: "10 session punch card that can be used at any time within 3 months. Your most flexible plan for clients who want access without a recurring commitment.",
  benefits: ["Applies to classes and open gym", "One time purchase"],
  recommendedPrice: null,
  planType: "session-pack",
  frequency: "10 sessions",
  planFormValues: {
    name: "10 Session Punch Card",
    clientStatus: clientStatusOptions[0],
    price: 0,
    billEveryCount: 1,
    billEveryInterval: intervalTypeOptions[2],
    openGym: true,
    classAccess: classAccessOptions[0],
    recursForever: false,
    occurrences: 1,
    sessionsCount: 10,
    sessionsExpire: true,
    sessionsExpireCount: 3,
    sessionsExpireInterval: intervalTypeOptions[2],
    attendanceLimit: attendanceLimitOptions[1],
    shortDescription: "10-class punch card valid for 3 months. Use for any class or open gym session with no recurring commitment.",
    longDescription: `<p><strong>10 Session Punch Card Details</strong></p><p>Our 10 Session Punch Card offers maximum flexibility without a recurring commitment. Perfect for those with variable schedules or who want to supplement their primary fitness routine.</p><p><strong>What is included:</strong></p><p>- 10 sessions to use for any regular classes or open gym</p><p>- Valid for 3 months from date of purchase</p><p>- Sessions tracked automatically in our system</p><p><strong>Payment Information:</strong></p><p>- One-time payment</p><p>- No recurring charges or commitments</p><p><strong>Usage Policies:</strong></p><p>- All 10 sessions must be used within 3 months</p><p>To use your sessions, simply check in at the front desk or through our app before each class. Our system will automatically track your remaining sessions and expiration date.</p><p>This plan is ideal for people with changing schedules, those who travel frequently, or anyone who wants to experience our facility without a monthly commitment.</p>`,
    confirmationMessage: `<p>Thanks for purchasing your 10 Session Punch Card! This flexible option gives you the freedom to attend classes on your schedule.</p><p>Your punch card details:</p><p>- 10 sessions to use for any class or open gym time</p><p>- Valid for 3 months from today</p><p>- No recurring commitment</p><p>To use your sessions, simply check in at the front desk or through our app before each class. The system will automatically track your remaining sessions.</p><p>We look forward to helping you on your fitness journey. See you at your next session!</p>`,
    ...sharedPlanFormValues,
  },
};

export const DROP_IN_PLAN_TEMPLATE: PlanTemplate = {
  id: "drop-in",
  name: "Drop In",
  apiName: "Drop In",
  description: "Drop in to a single class or open gym session. Best for clients who want to try out the gym.",
  benefits: ["Classes or open gym", "One time purchase"],
  recommendedPrice: null,
  planType: "session-pack",
  frequency: "single session",
  planFormValues: {
    name: "Drop In",
    clientStatus: clientStatusOptions[1],
    price: 0,
    billEveryCount: 1,
    billEveryInterval: intervalTypeOptions[2],
    openGym: true,
    recursForever: false,
    occurrences: 1,
    classAccess: classAccessOptions[0],
    sessionsCount: 1,
    sessionsExpire: true,
    sessionsExpireCount: 1,
    sessionsExpireInterval: intervalTypeOptions[2],
    attendanceLimit: attendanceLimitOptions[1],
    shortDescription: "Single session access to any class or open gym. Perfect for visitors or those wanting to experience our facility.",
    longDescription: `<p><strong>Drop-In Pass Details</strong></p><p>Our Drop-In pass gives you access to a single class or open gym session. Perfect for visitors, friends of members, or those wanting to experience our facility before committing to a membership.</p><p><strong>What is included:</strong></p><p>- One-time access to any regular class or open gym session</p><p>- Full use of all equipment during your visit</p><p>- Brief introduction to our facility for first-time visitors</p><p><strong>Important Information:</strong></p><p>- Valid for one month from date of purchase</p><p>- Reservations recommended to secure your spot</p><p>- Please arrive early if it's your first visit</p><p><strong>Policies:</strong></p><p>- Please complete the online waiver before arriving</p><p>Drop-in visitors are welcome to participate in all regular class activities and will receive the same quality coaching as our members. Our community is friendly and supportive of newcomers!</p>`,
    confirmationMessage: `<p>Thanks for purchasing your drop-in pass! We're excited to have you visit our facility.</p><p>Please reserve your spot in a class so we know you're coming. Your pass is valid for one class or open gym session and expires in 1 month from purchase.</p><p>When you arrive, please check in at the front desk 10 minutes before your scheduled session. First-time visitors should arrive 15 minutes early to complete a brief orientation.</p><p>We hope you enjoy your experience with us. Let us know if you have any questions before your visit!</p>`,
    ...sharedPlanFormValues,
  },
};

export const FREE_TRIAL_PLAN_TEMPLATE: PlanTemplate = {
  id: "free-trial",
  name: "Free Trial",
  description: "Free trial plan. Best for clients who want to try out the gym.",
  benefits: ["Classes or open gym", "One time purchase"],
  recommendedPrice: 0,
  planType: "session-pack",
  frequency: "single session",
  planFormValues: {
    name: "Free Trial",
    clientStatus: clientStatusOptions[2],
    price: 0,
    billEveryCount: 1,
    billEveryInterval: intervalTypeOptions[2],
    openGym: true,
    recursForever: false,
    occurrences: 1,
    classAccess: classAccessOptions[0],
    sessionsCount: 1,
    sessionsExpire: true,
    sessionsExpireCount: 1,
    sessionsExpireInterval: intervalTypeOptions[2],
    shortDescription: "Try one free class or open gym session. Experience our community and training style with no obligation.",
    attendanceLimit: attendanceLimitOptions[1],
    longDescription: `<p><strong>Free Trial Details</strong></p><p>Our Free Trial gives you the perfect opportunity to experience our facility, coaching style, and community atmosphere before making any commitment. We're confident you'll love what we offer!</p><p><strong>What is included:</strong></p><p>- One complimentary class or open gym session</p><p>- Brief facility tour and introduction</p><p>- Information about membership options</p><p><strong>Important Information:</strong></p><p>- Valid for one month from date of sign-up</p><p>- First-time visitors only (one trial per person)</p><p>- Reservation required - book through our app or website</p><p><strong>What to Bring:</strong></p><p>- Comfortable workout clothes</p><p>- Water bottle</p><p>- Athletic shoes</p><p>We recommend completing your waiver online before arriving to streamline your check-in process. Our coaches will ensure your workout is appropriate for your fitness level - no experience necessary!</p><p>After your trial, our team will be happy to discuss membership options that fit your goals and schedule. If you decide to join, we'll help you get set up and integrated into our community right away.</p>`,
    confirmationMessage: `<p>Thank you for signing up for a free trial at our gym! We're excited to have you join our community and can't wait to help you start your fitness journey.</p><p>Your free trial includes access to one class or open gym session of your choice. To get started, please download our member app so you can view our schedule and reserve your spot.</p><p>The trial is valid for 1 month from today. We recommend booking your session soon to experience everything we have to offer.</p><p>If you have any questions or need assistance, feel free to reach out. We look forward to seeing you in the gym soon!</p>`,
    ...sharedPlanFormValues,
  },
};

